// src/components/resources/glossary/MetricsCalculation.tsx

'use client'

import { motion } from 'motion/react'
import type { BaseGlossaryTerm } from '~/lib/resources/types/shared/base'
import type { MetricCalculation, MetricComponent } from '~/lib/resources/types/glossary/terms'
import { cn } from '~/lib/utils'

/**
 * Accepts any glossary term that carries a `MetricCalculation`. Was
 * `MetricTerm & { category: 'metrics' }` — too narrow now that the
 * Q2 2026 Tier-1 enrichment widened `calculation` to creative +
 * general terms (Creative Fatigue, Incrementality). The component
 * body only reads `term.calculation`, so the prop type just needs
 * that field — no category constraint. Drops the dishonest
 * `category: 'metrics' as const` cast at the call site in
 * TabContent.tsx that papered over the prop-type mismatch.
 */
interface MetricsCalculationProps {
  term: BaseGlossaryTerm & { calculation: MetricCalculation }
}

interface ValidComponent extends MetricComponent {
  name: string
  description: string
}

function isValidComponent(component: MetricComponent): component is ValidComponent {
  return typeof component.name === 'string' && 
         component.name.length > 0 && 
         typeof component.description === 'string'
}

export default function MetricsCalculation({ term }: MetricsCalculationProps): JSX.Element | null {
  if (!term.calculation) return null

  const { formula = '', components = [], variables = [] } = term.calculation

  // Create a map of identifier names → tooltip description. Both
  // `components` (the structured breakdown rendered below) and
  // `variables` (free-form mentions like `p-value` that appear in the
  // formula but don't need a numbered breakdown row) feed the lookup
  // so the parser recognises BOTH as named chips. Without `variables`,
  // hyphenated names like `p-value` get split by the operator pass and
  // lose their semantics — `r3317528471`.
  const validComponents = components.filter(isValidComponent)
  const identifierLookup = new Map<string, { description?: string }>()
  for (const c of validComponents) {
    identifierLookup.set(c.name, { description: c.description })
  }
  for (const v of variables) {
    if (
      typeof v.name === 'string' &&
      v.name.length > 0 &&
      !identifierLookup.has(v.name)
    ) {
      identifierLookup.set(v.name, { description: typeof v.description === 'string' ? v.description : undefined })
    }
  }

  // Find the longest matching identifier name at a position.
  // Matched BEFORE the operator pass so multi-character identifiers
  // that contain operator chars (e.g. `p-value`, `Incremental Lift %`)
  // are captured as a single token instead of being split.
  function findIdentifierAtPosition(text: string, startPos: number): { match: string; endPos: number } | null {
    let longestMatch: string | null = null
    let endPos = startPos
    for (const [name] of identifierLookup.entries()) {
      if (text.startsWith(name, startPos)) {
        if (!longestMatch || name.length > longestMatch.length) {
          longestMatch = name
          endPos = startPos + name.length
        }
      }
    }
    return longestMatch ? { match: longestMatch, endPos } : null
  }

  // Parse formula into parts, preserving multi-word components, comparison
  // operators, and free-text terms that aren't formal component names.
  //
  // The previous parser dropped any character it didn't recognize as
  // operator/number/component (so a formula like `p-value < Significance
  // Level` rendered as just the chip "Significance Level" with `p-value`
  // and `<` silently eaten). The Q2 2026 enrichment also surfaced
  // formulas that use comparison operators which weren't in the operator
  // set. This pass:
  //   1. Expands operators to include `<`, `>`, `≤`, `≥`, `.`, `,`.
  //   2. Decimal numbers like `0.05` parse as one number token.
  //   3. Unrecognized character runs (e.g. `p-value`) accumulate into a
  //      `text` token instead of being discarded — preserves the
  //      authored formula verbatim even when no component name matches.
  const formulaParts: { type: 'component' | 'operator' | 'number' | 'text'; content: string }[] = []
  let pos = 0

  // Check if formula starts with term name and equals sign
  const equalsIndex = formula.indexOf('=')
  if (equalsIndex > 0) {
    // Add term name as text
    formulaParts.push({ type: 'text', content: formula.slice(0, equalsIndex).trim() })
    // Add equals sign as operator
    formulaParts.push({ type: 'operator', content: '=' })
    // Start parsing from after equals sign
    pos = equalsIndex + 1
  }

  const OPERATOR_RE = /[+\-*/()=×÷%<>≤≥,]/
  // `pendingText` accumulates consecutive characters that don't match any
  // structured token so we can emit them as a single text run when the
  // next operator/component/number boundary hits.
  let pendingText = ''
  function flushPendingText(): void {
    const trimmed = pendingText.trim()
    if (trimmed) formulaParts.push({ type: 'text', content: trimmed })
    pendingText = ''
  }

  while (pos < formula.length) {
    const ch = formula.charAt(pos)

    // Whitespace flushes any pending text run.
    if (/\s/.test(ch)) {
      flushPendingText()
      pos++
      continue
    }

    // Try to match a named identifier (component or variable) FIRST so
    // multi-character names that contain operator chars — e.g.
    // `p-value`, `Incremental Lift %` — are captured as a single token
    // before the per-character operator pass eats their hyphens or
    // percent signs. `r3317528471`.
    const identifierMatch = findIdentifierAtPosition(formula, pos)
    if (identifierMatch) {
      flushPendingText()
      formulaParts.push({ type: 'component', content: identifierMatch.match })
      pos = identifierMatch.endPos
      continue
    }

    // Operators
    if (OPERATOR_RE.test(ch)) {
      flushPendingText()
      formulaParts.push({ type: 'operator', content: ch })
      pos++
      continue
    }

    // Numbers (including decimals like 0.05)
    if (/\d/.test(ch)) {
      flushPendingText()
      let number = ''
      while (
        pos < formula.length &&
        (/\d/.test(formula.charAt(pos)) ||
          (formula.charAt(pos) === '.' && /\d/.test(formula.charAt(pos + 1))))
      ) {
        number += formula.charAt(pos)
        pos++
      }
      formulaParts.push({ type: 'number', content: number })
      continue
    }

    // Unrecognized character — accumulate into the pending text run so
    // multi-character terms not in the identifier lookup still render
    // together rather than being silently dropped.
    pendingText += ch
    pos++
  }
  flushPendingText()

  return (
    <div className="space-y-6">
      {/* Formula */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50">
        <h3 className="text-xl font-semibold mb-4">Formula</h3>
        <div className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
          {formulaParts.map((part, index) => {
            if (part.type === 'operator' || part.type === 'number' || part.type === 'text') {
              return (
                <span 
                  key={index}
                  className={cn(
                    "text-lg font-mono",
                    part.type === 'text' 
                      ? "text-gray-900 dark:text-white font-medium"
                      : "text-gray-400 dark:text-gray-600"
                  )}
                >
                  {part.content}
                </span>
              )
            }

            // Look up the tooltip description in the merged identifier
            // lookup (components ∪ variables) so chips for variable-
            // only identifiers like `p-value` still hover-explain.
            const component = identifierLookup.get(part.content)
            
            return (
              <div
                key={index}
                className="group relative inline-flex"
              >
                <motion.span
                  className="cursor-help rounded-md bg-indigo-50 px-2 py-1 text-indigo-700 
                           transition-colors hover:bg-indigo-100
                           dark:bg-indigo-900/30 dark:text-indigo-300 
                           dark:hover:bg-indigo-900/40"
                  whileHover={{ scale: 1.05 }}
                >
                  {part.content}
                </motion.span>

                {component?.description && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute left-0 top-full z-10 mt-2 hidden w-64 rounded-lg 
                             border border-gray-200 bg-white p-4 shadow-lg 
                             group-hover:block dark:border-gray-700 dark:bg-gray-800"
                  >
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {component.description}
                    </p>
                  </motion.div>
                )}
              </div>
            )
          })}
        </div>

        {/* Explanation */}
        {term.calculation.explanation && (
          <div className="mt-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {term.calculation.explanation}
            </p>
          </div>
        )}
      </div>

      {/* Components Breakdown */}
      {validComponents.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900/50">
          <h3 className="text-xl font-semibold mb-4">Components Breakdown</h3>
          <ul className="space-y-4">
            {validComponents.map((component, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-sm font-medium text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
                  {index + 1}
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {component.name}
                  </div>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">
                    {component.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}